export default {
  // Page title
  myLifestyle: 'My Lifestyle',
  
  // Page titles and headers
  pageTitle: 'Lifestyle Habits',
  subtitle: 'Here you can see what it\'s about',
  lifestyleTitle: 'My Lifestyle',
  lifestyleSubtitle: 'Assess your current lifestyle',
  
  // Section titles
  sections: {
    fitness: 'Fitness',
    food: 'Food & Nutrition',
    alcohol: 'Alcohol',
    smoking: 'Smoking',
    sleep: 'Sleep',
    stress: 'Stress Management',
    social: 'Social Life',
    workLifeBalance: 'Work-Life Balance'
  },
  
  // Instructions
  instructions: 'You can jump between different sections after clicking START.',
  startInstructions: 'Click START to begin the lifestyle assessment',
  sectionInstructions: 'Complete each section to get a comprehensive view',
  
  // Button labels
  start: 'Start',
  next: 'Next',
  previous: 'Previous',
  skip: 'Skip',
  finish: 'Finish',
  continue: 'Continue',
  back: 'Back',
  
  // Loading states
  loadingSections: 'Loading sections...',
  loadingQuestions: 'Loading lifestyle questions from server... This may take a few seconds on first load.',
  processingAnswers: 'Processing your answers...',
  savingProgress: 'Saving your progress...',
  
  // Progress indicators
  section: 'Section',
  of: 'of',
  question: 'Question',
  questions: 'Questions',
  progress: 'Progress',
  
  // Question types
  questionTypes: {
    text: 'Text Input',
    multipleChoice: 'Multiple Choice',
    scale: 'Scale',
    yesNo: 'Yes/No',
    frequency: 'Frequency',
    amount: 'Amount',
    time: 'Time',
    custom: 'Custom'
  },
  
  // Common responses
  responses: {
    yes: 'Yes',
    no: 'No',
    sometimes: 'Sometimes',
    rarely: 'Rarely',
    often: 'Often',
    always: 'Always',
    never: 'Never',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    yearly: 'Yearly'
  },
  
  // Error messages
  errorLoading: 'Error loading lifestyle questions',
  errorSaving: 'Error saving your answers',
  networkError: 'Network error. Please check your connection.',
  serverError: 'Server error. Please try again later.',
  errorMessages: {
    snackQuestion: 'You have not answered the question about your snacks.',
    agilityQuestions: 'You have not answered all mobility questions.',
    allQuestionParts: 'You have not answered all parts of the question.',
    questionNotAnswered: 'You have not answered the question.',
    unansweredQuestions: 'You have not answered all questions ({count} remaining).'
  },
  
  // Success messages
  answersSaved: 'Your answers have been saved',
  sectionCompleted: 'Section completed',
  allSectionsCompleted: 'All sections completed!',
  
  // Help text
  help: 'Help',
  tips: 'Tips',
  moreInfo: 'More Information',
  privacyNote: 'Your answers are private and secure',
  
  // Navigation
  home: 'Home',
  results: 'Results',
  overview: 'Overview',
  
  // Results
  resultsTitle: 'Your Lifestyle Results',
  resultsSubtitle: 'Here\'s what we found about your lifestyle',
  recommendations: 'Recommendations',
  improvements: 'Areas for Improvement',
  strengths: 'Your Strengths',
  showResults: 'Show results',
  goodJobSeeResults: 'Good job, see the results!',
  clickOnPieSlices: 'Click the pie slices to see detailed results',
  selectAreaForDetails: 'Select an area to see detailed information:',
  showDetails: 'Show details',
  showChart: 'Show chart',
  notAnswered: 'Not answered',
  unanswered: 'Unanswered',
  noInformationAvailable: 'No information available for this area',
  
  // Categories
  categories: {
    physical: 'Physical Health',
    mental: 'Mental Health',
    social: 'Social Well-being',
    environmental: 'Environmental Factors'
  },
  
  // Health values
  healthValues: {
    riskValue: 'Risk value',
    healthValue: 'Health value',
    riskOrHealthValue: 'Risk or health value',
    onAlcohol: 'on alcohol',
    riskValueToHealth: '(risk value) to 5 for physical activity',
    youHaveValue: 'You have {label} value',
    risk: 'Risk',
    healthy: 'Healthy',
    warning: 'warning',
    value: 'Value'
  },

  // Section introductions
  physicalTestIntro: 'Great! Now we\'ll do some simple physical tests and questions about your lifestyle.',
  fillValueInstruction: '(Fill in your value and then click Select)',
  physicalActivitySummary: 'Summary Physical Activity',
  physicalActivityDescription: 'Average of my fitness, physical training, daily activities, sedentary time.',
  physicalActivityDescriptionNoFitness: 'Average of physical training, daily activities, sedentary time.',
  physicalTestsSummary: 'Summary Physical Tests',
  physicalTestsSummaryDescription: 'Average of your answers in the physical capacity tests.',
  kasamSummary: 'Summary KASAM',
  kasamSummaryDescription: 'Average of your answers about sense of coherence.',
  perceivedHealthSummary: 'Summary Perceived Health',
  perceivedHealthSummaryDescription: 'Average of your answers about how you experience your health.',
  dependencyAreasSummary: 'Summary Dependency Areas',
  dependencyAreasDescription: 'Average of your answers in dependency areas.',
  foodEnergySummary: 'Summary Food and Energy',
  foodEnergyDescription: 'Average of your answers about food, energy, and fluids.',
  leisureLifeSummary: 'Summary Life in Leisure Time',
  leisureLifeDescription: 'Average of your answers about leisure time, screen time, and relationships.',
  workLifeArea: 'Work life',
  workLifeSummary: 'Summary Work life',
  workLifeDescription: 'Average of your answers about work life.',
  physicalActivityIntro: 'Now we\'ll take some questions about how much you move.',
  eatingHabitsIntro: 'Now to your eating habits.',
  energyBalanceQuestion: 'Are you eating too little or too much relative to how much you move?',
  kasamIntro: 'Now an exciting test about whether you feel life is comprehensible, manageable and meaningful.',
  notAnsweredOtherQuestions: 'You have not answered the other questions.',
  clickHereToSee: 'Click here to see',
  clickOnImageToZoom: 'Click on the image to zoom in on the text',
  dragToMove: 'Drag to move the image',
  
  // Lifestyle section headers (page labels) - with safe keys
  sectionLabel_kasam: 'Sense of Coherence KASAM',
  sectionLabel_physicalTests: 'Physical Capacity - Tests',
  sectionLabel_physicalQuestions: 'Physical Capacity - Questions',
  sectionLabel_perceivedHealth: 'Perceived Health',
  sectionLabel_dependencies: 'Dependencies',
  sectionLabel_foodEnergy: 'Food and Energy',
  sectionLabel_leisureLife: 'Life in Leisure Time',
  
  // Legacy sectionLabels (keeping for backwards compatibility)
  sectionLabels: {
    'Känsla av sammanhang KASAM': 'Sense of Coherence KASAM',
    'Fysisk kapacitet - tester': 'Physical Capacity - Tests',
    'Fysisk kapacitet - frågor': 'Physical Capacity - Questions',
    'Upplevd hälsa': 'Perceived Health',
    'Beroendeområden': 'Dependencies',
    'Mat och energi': 'Food and Energy',
    'Livet på fritiden': 'Life in Leisure Time',
  },
  
  // Lifestyle factor labels (fallback translations for database labels)
  factorLabels: {
    // Physical capacity
    'Fysisk kapacitet - frågor': 'Physical Capacity - Questions',
    'Fysisk kapacitet - tester': 'Physical Capacity - Tests',
    'Min kondition': 'My Fitness',
    'Fysisk träning': 'Physical Training',
    'Vardagsaktiviteter': 'Daily Activities',
    'Stillasittande': 'Sedentary Time',
    'Resultat Fysisk Aktivitet': 'Physical Activity Result',
    'Balans': 'Balance',
    'Hållning': 'Posture',
    'Rörlighet': 'Mobility',
    'Styrka': 'Strength',
    'Styrka Ben': 'Leg Strength',
    'Styrka Armar': 'Arm Strength',
    'Styrka Mage': 'Core Strength',
    strengthArm: 'Arm Strength',
    strengthCore: 'Core Strength',
    'Kondition': 'Fitness',
    
    // Nutrition
    'Matvanor': 'Eating Habits',
    'Energiintag': 'Energy Intake',
    'Energibehov': 'Energy Needs',
    'Energibalans': 'Energy Balance',
    'Mat och energi': 'Food and Energy',
    'Mina måltidsvanor': 'My Meal Habits',
    'Matinnehåll': 'Food Content',
    'Matmängd': 'Food Amount',
    'Extra energiintag': 'Extra Energy Intake',
    
    // Dependencies
    'Alkohol': 'Alcohol',
    'Rökning': 'Smoking',
    'Snusning': 'Snuff',
    'Beroendeområden': 'Dependency Areas',
    'Receptfria läkemedel': 'Over-the-counter Medications',
    'Non-prescription medicines': 'Over-the-counter Medications',
    nonPrescriptionMedicines: 'Over-the-counter Medications',
    'Spel om pengar': 'Gambling',
    
    // Health perception
    'Upplevd hälsa': 'Perceived Health',
    'Välmående': 'Well-being',
    'Min kropp': 'My Body',
    'Axlar, nacke, rygg': 'Shoulders, Neck, Back',
    'Utvilad': 'Rested',
    'Magont': 'Stomach Ache',
    'Huvudvärk': 'Headache',
    'Sömn': 'Sleep',
    'Stress i livet': 'Stress in Life',
    'Stress': 'Stress',
    
    // Leisure
    'Livet på fritiden': 'Leisure Life',
    'Fritidsaktiviteter': 'Leisure Activities',
    'Skärmtid': 'Screen Time',
    'Vänrelationer': 'Friend Relations',
    
    // KASAM
    'Känsla av sammanhang KASAM': 'Sense of Coherence KASAM',
    'KASAM': 'KASAM',
  },
}; 
